// SPDX-License-Identifier: Elastic-2.0
/**
 * Static fixture data for the in-browser MSW worker (`npm run dev:fixture`).
 *
 * Goal: a clickable demo of the SPA without any backend. Every payload
 * mirrors the production wire shape (committed OpenAPI snapshots), so
 * the UI exercises real type-checked code paths — only the network is
 * faked.
 *
 * Kept as plain const exports (no factory functions) so the data is
 * predictable across page reloads; the dev tester reasons about a
 * fixed world, not a randomised one.
 */
import type {
  Device,
  DeviceListResponse,
  Routine,
  RoutineListResponse,
  Suggestion,
  SuggestionListResponse,
} from "@/api/gateway";
import type { EntitlementResponse, UserResponse } from "@/api/accounts";

/** Wall-clock fixed in 2026-05-21 so timestamps stay consistent across reloads. */
const NOW_ISO = "2026-05-21T14:00:00.000Z";

/** Seeded local-only JWT used in `seed.ts` (mirrors the smoke spec's fake). */
export const FIXTURE_LOCAL_JWT = [
  "eyJhbGciOiJSUzI1NiIsImtpZCI6ImZpeHR1cmUifQ",
  "eyJzdWIiOiJmaXh0dXJlLXVzZXIiLCJleHAiOjQ5NDg4NzA0MDB9",
  "fixture-signature-not-verified-clientside",
].join(".");

/** Seeded cloud-mode session — JWT in the cloud slot so AuthGuard + EntitlementPanel both run. */
export const FIXTURE_CLOUD_JWT = FIXTURE_LOCAL_JWT;
export const FIXTURE_REFRESH_TOKEN = "fixture-refresh-token";

export const FIXTURE_USER: UserResponse = {
  user_id: "fixture-user",
  email: "demo@mirlai.local",
  display_name: "Demo User",
  tier: "plus",
  email_verified: true,
  created_at: "2026-01-15T00:00:00Z",
  updated_at: NOW_ISO,
};

export const FIXTURE_ENTITLEMENT: EntitlementResponse = {
  tier: "plus",
  status: "active",
  valid_until: "2026-11-21T00:00:00Z",
  period_started_at: "2026-05-01T00:00:00Z",
  cache_ttl_seconds: 300,
  caps: {
    cloud_llm_tokens_per_month: 500_000,
    cloud_llm_tokens_remaining_this_month: 312_300,
    instances_allowed: 3,
    backup_retention_days: 30,
  },
  features: {
    local_llm: false,
    pattern_detector: true,
    rule_based_suggester: true,
    anomaly_detector: "basic",
    remote_access: true,
    voice_control: true,
    mobile_app: "premium",
    automation_marketplace: "publish",
    fleet_dashboard: false,
    api_read: false,
    support_sla: "email-48h",
  },
};

/**
 * Devices — covers each major DeviceType so the dashboard exercises
 * every card variant. Rooms spread across 3 areas so the by-room
 * grouping renders ≥ 2 sections.
 */
const DEVICES: readonly Device[] = [
  {
    id: "light.living_room",
    type: "light",
    name: "Living-room lamp",
    room: "Living room",
    available: true,
    last_changed_at: NOW_ISO,
    state: { on_off: "off", brightness: 0 },
    controls: [
      { kind: "toggle", action: "on_off" },
      { kind: "slider", action: "brightness", min: 0, max: 100 },
    ] as unknown as Device["controls"],
  },
  {
    id: "switch.kitchen_coffee",
    type: "switch",
    name: "Coffee maker",
    room: "Kitchen",
    available: true,
    last_changed_at: NOW_ISO,
    state: { on_off: "on" },
    controls: [{ kind: "toggle", action: "on_off" }] as unknown as Device["controls"],
  },
  {
    id: "sensor.living_room_temp",
    type: "sensor",
    name: "Living-room temperature",
    room: "Living room",
    available: true,
    last_changed_at: NOW_ISO,
    state: { value: 22.5, unit: "°C" },
    controls: [],
  },
  {
    id: "lock.front_door",
    type: "lock",
    name: "Front door",
    room: "Entry",
    available: true,
    last_changed_at: NOW_ISO,
    state: { locked: true },
    controls: [{ kind: "toggle", action: "locked" }] as unknown as Device["controls"],
  },
  {
    id: "light.bedroom",
    type: "light",
    name: "Bedroom lamp",
    room: "Bedroom",
    available: false,
    last_changed_at: NOW_ISO,
    state: { on_off: "off" },
    controls: [{ kind: "toggle", action: "on_off" }] as unknown as Device["controls"],
  },
];

export const FIXTURE_DEVICES: DeviceListResponse = { devices: [...DEVICES] };

/** Routines — 2 typical bedtime / morning entries. */
const ROUTINES: readonly Routine[] = [
  {
    id: "routine-bedtime",
    name: "Bedtime",
    trigger_summary: "Every day at 23:00",
    action_summary: "Turn off Living-room lamp and Coffee maker",
    active: true,
  },
  {
    id: "routine-morning",
    name: "Morning brew",
    trigger_summary: "Weekdays at 07:30",
    action_summary: "Turn on Coffee maker",
    active: true,
  },
];

export const FIXTURE_ROUTINES: RoutineListResponse = { routines: [...ROUTINES] };

/** Suggestions — single high-confidence proposal so the approval flow has something to click. */
const SUGGESTIONS: readonly Suggestion[] = [
  {
    id: "suggestion-evening-lamp",
    description:
      "Turn the Living-room lamp on at sunset — we noticed you usually do this between 19:00 and 20:00.",
    confidence: 0.84,
    created_at: NOW_ISO,
    proposed_routine: {
      trigger: { kind: "sun", event: "sunset" },
      action: { device: "light.living_room", action: "on_off", value: "on" },
    },
  },
];

export const FIXTURE_SUGGESTIONS: SuggestionListResponse = {
  suggestions: [...SUGGESTIONS],
};
